import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Alert,
  Button,
  Checkbox,
  Collapse,
  Group,
  NumberInput,
  Stack,
  Text,
} from "@mantine/core";
import { IconAlertCircle, IconPlus, IconTrash } from "@tabler/icons-react";


interface TierBracket {
  min: number;
  max: number | null;
  providerPct: number;
  platformPct: number;
}

interface TierRulesManagerProps {
  enabled: boolean;
  brackets: TierBracket[];
  onEnabledChange: (enabled: boolean) => void;
  onBracketsChange: (brackets: TierBracket[]) => void;
  disabled?: boolean;
}

export const TierRulesManager = ({
  enabled,
  brackets,
  onEnabledChange,
  onBracketsChange,
  disabled = false,
}: TierRulesManagerProps) => {
  const { t } = useTranslation();

  const handleAddBracket = () => {
    const lastBracket = brackets[brackets.length - 1];
    const newMin = lastBracket ? (lastBracket.max ? lastBracket.max + 1 : 0) : 0;

    const newBracket: TierBracket = {
      min: newMin,
      max: null, // Unlimited by default
      providerPct: 84.0,
      platformPct: 16.0,
    };

    onBracketsChange([...brackets, newBracket]);
  };

  const handleRemoveBracket = (index: number) => {
    const newBrackets = brackets.filter((_, i) => i !== index);
    onBracketsChange(newBrackets);
  };

  const handleBracketChange = (index: number, field: keyof TierBracket, value: number | null) => {
    const newBrackets = [...brackets];
    newBrackets[index] = {
      ...newBrackets[index],
      [field]: value,
    };

    // Auto-calculate platform percentage if provider percentage changes
    if (field === "providerPct" && typeof value === "number") {
      newBrackets[index].platformPct = 100 - value;
    }

    onBracketsChange(newBrackets);
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Text size="sm" fw={500}>
          {t("tier-rules")}
        </Text>
        <Checkbox
          label={t("enable-tier-based-commissions")}
          checked={enabled}
          onChange={(e) => onEnabledChange(e.currentTarget.checked)}
          disabled={disabled}
        />
      </Group>

      <Collapse in={enabled}>
        <Stack gap="md">
          {/* Info Alert */}
          <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light">
            <Text size="xs">
              {t("tier-rules-info", "Tier earnings reset monthly on the 1st. Higher brackets unlock as providers earn more each month.")}
            </Text>
          </Alert>

          {/* Tier Brackets List */}
          {brackets.length === 0 && (
            <Text size="sm" c="dimmed" ta="center" py="md">
              {t("no-tier-brackets-configured")}
            </Text>
          )}

          {brackets.map((bracket, index) => (
            <Stack
              key={index}
              gap="xs"
              p="md"
              style={{
                border: "1px solid var(--clr-gray-200)",
                borderRadius: "var(--border-radius-3)",
                backgroundColor: "var(--clr-gray-50)",
              }}
            >
              <Group justify="space-between" align="center">
                <Text size="sm" fw={500}>
                  {t("tier")} {index + 1}
                </Text>
                {brackets.length > 1 && (
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="sm"
                    onClick={() => handleRemoveBracket(index)}
                    disabled={disabled}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                )}
              </Group>

              {/* Range Inputs */}
              <Group grow>
                <NumberInput
                  label={t("minimum-earnings")}
                  placeholder="0"
                  value={bracket.min}
                  onChange={(value) =>
                    handleBracketChange(index, "min", typeof value === "number" ? value : 0)
                  }
                  min={0}
                  disabled={disabled}
                  suffix=" CFA"
                />
                <NumberInput
                  label={t("maximum-earnings")}
                  placeholder={t("unlimited")}
                  value={bracket.max ?? undefined}
                  onChange={(value) =>
                    handleBracketChange(index, "max", typeof value === "number" ? value : null)
                  }
                  min={bracket.min + 1}
                  disabled={disabled}
                  suffix=" CFA"
                  description={!bracket.max ? t("unlimited") : ""}
                />
              </Group>

              {/* Commission Percentages */}
              <Group grow>
                <NumberInput
                  label={t("provider-percentage")}
                  placeholder="84.00"
                  value={bracket.providerPct}
                  onChange={(value) =>
                    handleBracketChange(
                      index,
                      "providerPct",
                      typeof value === "number" ? value : 0
                    )
                  }
                  min={0}
                  max={100}
                  step={0.01}
                  decimalScale={2}
                  disabled={disabled}
                  suffix=" %"
                />
                <NumberInput
                  label={t("platform-percentage")}
                  placeholder="16.00"
                  value={bracket.platformPct}
                  disabled
                  suffix=" %"
                  description={t("auto-calculated")}
                  styles={{
                    input: {
                      backgroundColor: "var(--clr-gray-100)",
                    },
                  }}
                />
              </Group>

              {/* Validation Warning */}
              {bracket.providerPct + bracket.platformPct !== 100 && (
                <Text size="xs" c="red">
                  {t("percentages-must-total-100")}
                </Text>
              )}
            </Stack>
          ))}

          {/* Add Bracket Button */}
          <Button
            variant="light"
            leftSection={<IconPlus size={16} />}
            onClick={handleAddBracket}
            disabled={disabled}
          >
            {t("add-tier-bracket")}
          </Button>
        </Stack>
      </Collapse>
    </Stack>
  );
};
